import styled, { css, keyframes } from 'styled-components';

/* ------------------------------- Layout -------------------------------- */

export const Container = styled.div<{ $narrow?: boolean }>`
  width: 100%;
  max-width: ${({ $narrow }) => ($narrow ? '880px' : '1200px')};
  margin: 0 auto;
  padding-left: 1.25rem;
  padding-right: 1.25rem;

  @media (min-width: 768px) {
    padding-left: 2rem;
    padding-right: 2rem;
  }
`;

export const Section = styled.section<{ $muted?: boolean; $tight?: boolean }>`
  padding-top: ${({ $tight }) => ($tight ? '2.5rem' : '4.5rem')};
  padding-bottom: ${({ $tight }) => ($tight ? '2.5rem' : '4.5rem')};
  background: ${({ theme, $muted }) => ($muted ? theme.colors.surface : 'transparent')};

  @media (max-width: 768px) {
    padding-top: ${({ $tight }) => ($tight ? '2rem' : '3rem')};
    padding-bottom: ${({ $tight }) => ($tight ? '2rem' : '3rem')};
  }
`;

/* ------------------------------ Headings ------------------------------- */

export const Eyebrow = styled.span`
  display: inline-block;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 0.75rem;
`;

export const SectionTitle = styled.h2`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: clamp(1.6rem, 3vw, 2.25rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.foreground};
  line-height: 1.15;
`;

export const SectionSubtitle = styled.p`
  margin-top: 0.75rem;
  font-size: 1.05rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 60ch;
  line-height: 1.6;
`;

export const SectionHeader = styled.div<{ $center?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $center }) => ($center ? 'center' : 'flex-start')};
  text-align: ${({ $center }) => ($center ? 'center' : 'left')};
  margin-bottom: 2.5rem;
`;

/* -------------------------------- Card --------------------------------- */

export const Card = styled.div<{ $interactive?: boolean }>`
  background: ${({ theme }) => theme.colors.card};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.card};
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  ${({ $interactive, theme }) =>
    $interactive &&
    css`
      cursor: pointer;
      &:hover {
        transform: translateY(-4px);
        box-shadow: ${theme.shadows.hover};
        border-color: ${theme.colors.neutral[300]};
      }
    `}
`;

/* ------------------------------- Badge --------------------------------- */

export const Badge = styled.span<{ $variant?: 'brand' | 'neutral' | 'success' | 'danger' | 'glass' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  padding: 0.3rem 0.65rem;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  line-height: 1;
  white-space: nowrap;

  ${({ $variant = 'brand', theme }) => {
    switch ($variant) {
      case 'neutral':
        return css`
          background: ${theme.colors.neutral[100]};
          color: ${theme.colors.neutral[700]};
        `;
      case 'success':
        return css`
          background: #dcfce7;
          color: #15803d;
        `;
      case 'danger':
        return css`
          background: #fee2e2;
          color: #b91c1c;
        `;
      case 'glass':
        return css`
          background: rgba(255, 255, 255, 0.9);
          color: ${theme.colors.neutral[800]};
          backdrop-filter: blur(8px);
          box-shadow: ${theme.shadows.sm};
        `;
      default:
        return css`
          background: ${theme.colors.primarySoft};
          color: ${theme.colors.primaryDark};
        `;
    }
  }}
`;

/* -------------------------------- Stat --------------------------------- */

export const Stat = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.text.secondary};

  svg {
    color: ${({ theme }) => theme.colors.neutral[400]};
    flex-shrink: 0;
  }
`;

/* ------------------------------- Button -------------------------------- */

export const Button = styled.button<{
  $variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'whatsapp';
  $size?: 'sm' | 'md' | 'lg';
  $full?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-family: ${({ theme }) => theme.fonts.primary};
  font-weight: 600;
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease,
    border-color 0.2s ease, color 0.2s ease;
  white-space: nowrap;
  width: ${({ $full }) => ($full ? '100%' : 'auto')};
  text-decoration: none;

  ${({ $size = 'md' }) => {
    switch ($size) {
      case 'sm':
        return css`
          font-size: 0.85rem;
          padding: 0.5rem 0.9rem;
        `;
      case 'lg':
        return css`
          font-size: 1rem;
          padding: 0.9rem 1.6rem;
        `;
      default:
        return css`
          font-size: 0.92rem;
          padding: 0.7rem 1.25rem;
        `;
    }
  }}

  ${({ $variant = 'primary', theme }) => {
    switch ($variant) {
      case 'secondary':
        return css`
          background: ${theme.colors.neutral[900]};
          color: #fff;
          &:hover {
            background: ${theme.colors.neutral[800]};
          }
        `;
      case 'outline':
        return css`
          background: transparent;
          color: ${theme.colors.foreground};
          border-color: ${theme.colors.border};
          &:hover {
            border-color: ${theme.colors.neutral[400]};
            background: ${theme.colors.surface};
          }
        `;
      case 'ghost':
        return css`
          background: transparent;
          color: ${theme.colors.foreground};
          &:hover {
            background: ${theme.colors.surface};
          }
        `;
      case 'whatsapp':
        return css`
          background: #25d366;
          color: #fff;
          &:hover {
            background: #1ebe5b;
          }
        `;
      default:
        return css`
          background: ${theme.colors.primary};
          color: ${theme.colors.onPrimary};
          box-shadow: ${theme.shadows.sm};
          &:hover {
            background: ${theme.colors.primaryDark};
            transform: translateY(-1px);
            box-shadow: ${theme.shadows.md};
          }
        `;
    }
  }}

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
  }
`;

/* ------------------------------- Inputs -------------------------------- */

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

export const Label = styled.label`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutral[700]};
`;

const inputBase = css`
  width: 100%;
  height: 46px;
  padding: 0 0.9rem;
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.foreground};
  background: ${({ theme }) => theme.colors.card};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &::placeholder {
    color: ${({ theme }) => theme.colors.neutral[400]};
  }
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primarySoft};
  }
`;

export const Input = styled.input`
  ${inputBase}
`;

export const Select = styled.select`
  ${inputBase}
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.7rem center;
  padding-right: 2.2rem;
  cursor: pointer;
`;

export const Textarea = styled.textarea`
  ${inputBase}
  height: auto;
  min-height: 120px;
  padding: 0.75rem 0.9rem;
  resize: vertical;
  line-height: 1.5;
`;

/* --------------------------- Loading / Skeleton ------------------------ */

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const Spinner = styled.div<{ $size?: number }>`
  width: ${({ $size = 24 }) => $size}px;
  height: ${({ $size = 24 }) => $size}px;
  border: 3px solid ${({ theme }) => theme.colors.neutral[200]};
  border-top-color: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

export const Skeleton = styled.div<{ $h?: string; $w?: string; $radius?: string }>`
  height: ${({ $h = '1rem' }) => $h};
  width: ${({ $w = '100%' }) => $w};
  border-radius: ${({ $radius, theme }) => $radius || theme.borderRadius.sm};
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.neutral[100]} 25%,
    ${({ theme }) => theme.colors.neutral[200]} 37%,
    ${({ theme }) => theme.colors.neutral[100]} 63%
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.4s ease infinite;
`;
